// ============================================================
// VidyaTrack — Landing Page  (v4 — contact-first, no login)
// SEO optimised · Detached from app · Nepal-focused
// ============================================================

import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { FiCheck, FiArrowRight } from "react-icons/fi";
import LandingNav    from "./components/LandingNav";
import LandingFooter from "./components/LandingFooter";
import useSEO        from "../seo/useSEO";
import {
  FEATURES, ROLES, PLANS, TESTIMONIALS,
  HIGHLIGHTS, HOW_IT_WORKS, CONTACT_INFO,
} from "./data/landingData";

/* ── palette ──────────────────────────────────────────────── */
const P = {
  primary:"#054bb4", medium:"#2e5caf", light:"#658cc2",
  xl:"#e8eef8", xxl:"#f4f7fd",
  text:"#111827", textMed:"#374151", textLight:"#6b7280",
  border:"#e4e9f2", success:"#10b981",
};

/* ── tiny helpers ─────────────────────────────────────────── */
const Sec = ({ id, bg="#fff", children, py=88 }) => (
  <section id={id} style={{ padding:`${py}px max(20px,calc((100vw - 1140px)/2))`, background:bg }}>
    {children}
  </section>
);

const Head = ({ tag, title, sub, light=false }) => (
  <div style={{ textAlign:"center", marginBottom:52 }}>
    <div style={{ color:light?"rgba(255,255,255,0.55)":P.primary, fontWeight:700, fontSize:"0.72rem",
      textTransform:"uppercase", letterSpacing:"1.4px", marginBottom:12 }}>{tag}</div>
    <h2 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800,
      fontSize:"clamp(1.6rem,4vw,2.4rem)", marginBottom:14,
      color:light?"#fff":P.text, lineHeight:1.2 }}>{title}</h2>
    {sub && <p style={{ color:light?"rgba(255,255,255,0.65)":P.textLight,
      maxWidth:520, margin:"0 auto", lineHeight:1.75, fontSize:"0.97rem" }}>{sub}</p>}
  </div>
);

const CTABtn = ({ children, onClick, href, outline=false }) => {
  const base = {
    display:"inline-flex", alignItems:"center", gap:8,
    padding:"13px 28px", borderRadius:12, fontWeight:700,
    fontSize:"0.95rem", fontFamily:"Inter,sans-serif",
    cursor:"pointer", transition:"all .2s", textDecoration:"none",
    border: outline ? `2px solid ${P.primary}` : "none",
    background: outline ? "transparent" : "linear-gradient(135deg,#054bb4,#2e5caf)",
    color: outline ? P.primary : "#fff",
    boxShadow: outline ? "none" : "0 6px 20px rgba(5,75,180,.26)",
  };
  if (href) return <a href={href} style={base}
    onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; if(!outline) e.currentTarget.style.boxShadow="0 10px 28px rgba(5,75,180,.36)"; }}
    onMouseLeave={e=>{ e.currentTarget.style.transform="none"; if(!outline) e.currentTarget.style.boxShadow="0 6px 20px rgba(5,75,180,.26)"; }}>
    {children}
  </a>;
  return <button onClick={onClick} style={base}
    onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-2px)"; if(!outline) e.currentTarget.style.boxShadow="0 10px 28px rgba(5,75,180,.36)"; }}
    onMouseLeave={e=>{ e.currentTarget.style.transform="none"; if(!outline) e.currentTarget.style.boxShadow="0 6px 20px rgba(5,75,180,.26)"; }}>
    {children}
  </button>;
};

/* ═══════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const [contact, setContact]   = useState({ name:"", email:"", school:"", phone:"", plan:"", message:"" });
  const [sent,    setSent]      = useState(false);
  const [sending, setSending]   = useState(false);
  const [formErr, setFormErr]   = useState("");

  useSEO({
    description: "Cloud-based school management system for schools in Nepal: homework, attendance, notices, study materials & parent communication. Book a free demo.",
    path: "/",
  });

  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior:"smooth" });

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.school) {
      setFormErr("Please fill in your name, email and school name.");
      return;
    }
    setFormErr("");
    setSending(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name : contact.name,
          from_email: contact.email,
          school    : contact.school,
          phone     : contact.phone,
          plan      : contact.plan,
          message   : contact.message,
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setSent(true);
      setContact({ name:"", email:"", school:"", phone:"", plan:"", message:"" });
    } catch (err) {
      console.error("EmailJS error:", err);
      setFormErr("Failed to send — please email us directly at titaniumcode.np@gmail.com or call +977-9804044517");
    } finally {
      setSending(false);
    }
    setTimeout(() => setSent(false), 10000);
  };

  /* ── input style ── */
  const inp = {
    width:"100%", padding:"11px 14px", borderRadius:9,
    border:`1.5px solid ${P.border}`, fontSize:"0.9rem",
    fontFamily:"Inter,sans-serif", color:P.text,
    outline:"none", background:"#fff", boxSizing:"border-box",
    transition:"border-color .15s",
  };

  /* ─────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily:"Inter,sans-serif", color:P.text, overflowX:"hidden" }}>

      {/* Structured data (JSON-LD) lives statically in public/index.html so
          non-JS crawlers (GPTBot, ClaudeBot, PerplexityBot…) can read it too. */}

      <LandingNav scroll={scroll}/>

      <main>
      {/* ══ HERO ══════════════════════════════════════════════ */}
      <section id="hero" style={{
        paddingTop:128, paddingBottom:80,
        paddingLeft:"max(20px,calc((100vw - 1140px)/2))",
        paddingRight:"max(20px,calc((100vw - 1140px)/2))",
        background:"linear-gradient(160deg,#f4f7fd 0%,#e8eef8 55%,#f0f4fb 100%)",
        textAlign:"center", position:"relative", overflow:"hidden",
      }}>
        {/* BG blobs */}
        <div style={{ position:"absolute",width:600,height:600,borderRadius:"50%",
          background:"radial-gradient(circle,rgba(5,75,180,.07) 0%,transparent 70%)",
          top:-180,right:-120,pointerEvents:"none" }}/>
        <div style={{ position:"absolute",width:400,height:400,borderRadius:"50%",
          background:"radial-gradient(circle,rgba(5,75,180,.05) 0%,transparent 70%)",
          bottom:-120,left:-60,pointerEvents:"none" }}/>

        {/* Badge */}
        <div style={{ display:"inline-flex", alignItems:"center", gap:8,
          background:"rgba(5,75,180,.07)", borderRadius:100, padding:"7px 18px",
          fontSize:"0.8rem", fontWeight:600, color:P.primary, marginBottom:24,
          border:`1px solid rgba(5,75,180,.14)` }}>
          <span style={{ width:6, height:6, borderRadius:"50%", background:P.primary, display:"inline-block" }}/>
          School Management System · Made in Nepal
        </div>

        <h1 style={{
          fontFamily:"'Poppins',sans-serif", fontWeight:900,
          fontSize:"clamp(2rem,5.5vw,3.6rem)", lineHeight:1.13,
          marginBottom:22, maxWidth:820, margin:"0 auto 22px",
        }}>
          The Smartest Way to<br/>
          <span style={{ background:"linear-gradient(135deg,#054bb4 20%,#658cc2 80%)",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
            Manage Your School
          </span>
          <span style={{ position:"absolute", width:1, height:1, overflow:"hidden", clip:"rect(0 0 0 0)", whiteSpace:"nowrap" }}>
            {" "}— School Management System for Nepal
          </span>
        </h1>

        <p style={{ fontSize:"clamp(.95rem,2.5vw,1.12rem)", color:P.textMed,
          lineHeight:1.8, maxWidth:600, margin:"0 auto 40px" }}>
          VidyaTrack is a cloud-based <strong>school management system for schools in Nepal</strong>.
          Connect teachers, students and parents in one platform — homework, attendance, notices,
          study materials and reports, accessible from any device, any time.
        </p>

        <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:56 }}>
          <CTABtn onClick={()=>scroll("contact")}>Book a Free Demo <FiArrowRight/></CTABtn>
          <CTABtn outline onClick={()=>scroll("features")}>See All Features</CTABtn>
        </div>

        {/* Highlights bar (no vanity metrics) */}
        <ul aria-label="VidyaTrack highlights" style={{ display:"inline-flex", gap:0, listStyle:"none", padding:0, margin:0,
          background:"linear-gradient(135deg,#054bb4,#2e5caf)",
          borderRadius:16, overflow:"hidden", boxShadow:"0 8px 32px rgba(5,75,180,.25)",
          flexWrap:"wrap", justifyContent:"center" }}>
          {HIGHLIGHTS.map((h,i)=>(
            <li key={h.label} style={{ padding:"16px 26px", textAlign:"center", color:"#fff",
              display:"flex", alignItems:"center", gap:10,
              borderRight: i < HIGHLIGHTS.length-1 ? "1px solid rgba(255,255,255,.12)" : "none" }}>
              <span aria-hidden="true" style={{ fontSize:"1.15rem" }}>{h.icon}</span>
              <span style={{ fontSize:"0.85rem", fontWeight:600, letterSpacing:".2px" }}>{h.label}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ══ FEATURES ══════════════════════════════════════════ */}
      <Sec id="features" bg={P.xxl}>
        <Head tag="FEATURES" title="Everything Your School Needs"
          sub="One platform that replaces registers, WhatsApp groups, paper notices and scattered spreadsheets."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(250px,1fr))", gap:22 }}>
          {FEATURES.map(f=>(
            <article key={f.title} style={{ background:"#fff", borderRadius:14, padding:"24px 22px",
              border:`1px solid ${P.border}`, transition:"box-shadow .2s, transform .2s" }}
              onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 8px 32px rgba(5,75,180,.10)";e.currentTarget.style.transform="translateY(-3px)";}}
              onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.transform="none";}}>
              <div style={{ width:44, height:44, borderRadius:12, background:P.xl,
                display:"flex", alignItems:"center", justifyContent:"center",
                color:P.primary, marginBottom:14 }}>{f.icon}</div>
              <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:700, fontSize:"0.97rem",
                marginBottom:8, color:P.text }}>{f.title}</h3>
              <p style={{ fontSize:"0.83rem", color:P.textLight, lineHeight:1.7, margin:0 }}>{f.desc}</p>
            </article>
          ))}
        </div>
      </Sec>

      {/* ══ ROLES ═════════════════════════════════════════════ */}
      <Sec id="roles" bg="#fff">
        <Head tag="WHO IT'S FOR" title="Built for Every Role in Your School"
          sub="One system, four tailored experiences — each user sees exactly what they need."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:20 }}>
          {ROLES.map(r=>(
            <div key={r.label} style={{ borderRadius:14, padding:"24px 20px",
              background:r.light, border:`1.5px solid ${r.clr}20` }}>
              <div style={{ fontSize:"2rem", marginBottom:10 }}>{r.emoji}</div>
              <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.05rem",
                marginBottom:14, color:r.clr }}>{r.label}</h3>
              <ul style={{ listStyle:"none", padding:0, margin:0 }}>
                {r.points.map(p=>(
                  <li key={p} style={{ display:"flex", alignItems:"flex-start", gap:8,
                    fontSize:"0.83rem", color:P.textMed, marginBottom:7 }}>
                    <FiCheck size={13} style={{ color:r.clr, flexShrink:0, marginTop:2 }}/>{p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Sec>

      {/* ══ HOW IT WORKS ══════════════════════════════════════ */}
      <Sec id="how-it-works" bg="linear-gradient(150deg,#054bb4 0%,#1e3a8a 100%)" py={80}>
        <Head tag="HOW IT WORKS" title="Up and Running in One Day" light
          sub="We handle the full setup. You just bring your school."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:24 }}>
          {HOW_IT_WORKS.map(s=>(
            <div key={s.n} style={{ background:"rgba(255,255,255,.07)", borderRadius:16,
              padding:"28px 24px", border:"1px solid rgba(255,255,255,.12)" }}>
              <div style={{ fontFamily:"'Poppins',sans-serif", fontWeight:900, fontSize:"2rem",
                color:"rgba(255,255,255,.18)", marginBottom:12 }}>{s.n}</div>
              <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:700, fontSize:"1.05rem",
                color:"#fff", marginBottom:10 }}>{s.t}</h3>
              <p style={{ fontSize:"0.85rem", color:"rgba(255,255,255,.65)", lineHeight:1.7, margin:0 }}>{s.d}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center", marginTop:44 }}>
          <CTABtn onClick={()=>scroll("contact")}>Get Started Today <FiArrowRight/></CTABtn>
        </div>
      </Sec>

      {/* ══ PRICING ═══════════════════════════════════════════ */}
      <Sec id="pricing" bg={P.xxl}>
        <Head tag="PRICING" title="Simple, Transparent Pricing"
          sub="Per-student monthly pricing. No hidden fees. No contracts. Contact us to discuss your school's needs."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:24, alignItems:"start" }}>
          {PLANS.map(plan=>(
            <div key={plan.tier} style={{
              borderRadius:18,
              background: plan.featured ? "linear-gradient(150deg,#054bb4 0%,#1e40af 100%)" : plan.bg,
              border:`1.5px solid ${plan.border}`,
              padding:"28px 26px",
              transform: plan.featured ? "scale(1.04)" : "none",
              boxShadow: plan.featured ? "0 16px 48px rgba(5,75,180,.30)" : "none",
              position:"relative",
            }}>
              {plan.badge && (
                <div style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)",
                  background:"linear-gradient(135deg,#f59e0b,#d97706)", color:"#fff",
                  padding:"4px 16px", borderRadius:20, fontSize:"0.7rem", fontWeight:800,
                  letterSpacing:".5px", whiteSpace:"nowrap" }}>
                  {plan.badge}
                </div>
              )}
              <div style={{ fontSize:"1.8rem", marginBottom:8 }}>{plan.icon}</div>
              <div style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.1rem",
                color: plan.featured ? "#fff" : P.text, marginBottom:4 }}>{plan.tier}</div>
              <div style={{ fontSize:"0.78rem", color: plan.featured ? "rgba(255,255,255,.6)" : P.textLight,
                marginBottom:16 }}>{plan.tagline}</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:6 }}>
                <span style={{ fontFamily:"'Poppins',sans-serif", fontWeight:900,
                  fontSize: plan.price==="Contact Us" ? "1.4rem" : "2rem",
                  color: plan.featured ? "#fff" : P.primary }}>{plan.price}</span>
                {plan.suffix && <span style={{ fontSize:"0.75rem",
                  color: plan.featured ? "rgba(255,255,255,.55)" : P.textLight }}>{plan.suffix}</span>}
              </div>
              <div style={{ fontSize:"0.75rem", fontWeight:600,
                color: plan.featured ? "rgba(255,255,255,.7)" : P.light,
                marginBottom:20 }}>{plan.highlight}</div>
              <ul style={{ listStyle:"none", padding:0, margin:"0 0 24px" }}>
                {plan.features.map(f=>(
                  <li key={f} style={{ display:"flex", alignItems:"flex-start", gap:8,
                    fontSize:"0.83rem", marginBottom:8,
                    color: plan.featured ? "rgba(255,255,255,.85)" : P.textMed }}>
                    <FiCheck size={13} style={{ color: plan.featured ? "#6ee7b7" : P.success, flexShrink:0, marginTop:2 }}/>{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={()=>scroll("contact")}
                style={{
                  width:"100%", padding:"12px 0", borderRadius:10, border:"none",
                  cursor:"pointer", fontWeight:700, fontSize:"0.9rem",
                  fontFamily:"Inter,sans-serif",
                  background: plan.featured ? "#fff" : "linear-gradient(135deg,#054bb4,#2e5caf)",
                  color: plan.featured ? P.primary : "#fff",
                  transition:"all .2s",
                }}
                onMouseEnter={e=>e.currentTarget.style.opacity=".88"}
                onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                Contact Us to Enrol
              </button>
            </div>
          ))}
        </div>
        <p style={{ textAlign:"center", marginTop:28, fontSize:"0.83rem", color:P.textLight }}>
          All plans include full setup & onboarding support. Prices in Nepali Rupees (NPR).
        </p>
      </Sec>

      {/* ══ TESTIMONIALS ══════════════════════════════════════ */}
      <Sec id="testimonials" bg="#fff">
        <Head tag="TESTIMONIALS" title="What Schools Say About VidyaTrack"/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:22 }}>
          {TESTIMONIALS.map(t=>(
            <blockquote key={t.name} style={{ background:P.xxl, borderRadius:14,
              padding:"24px 22px", margin:0, border:`1px solid ${P.border}` }}>
              <p style={{ fontSize:"0.9rem", color:P.textMed, lineHeight:1.75,
                fontStyle:"italic", marginBottom:18 }}>"{t.text}"</p>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:38, height:38, borderRadius:"50%",
                  background:`linear-gradient(135deg,${t.clr},${t.clr}99)`,
                  color:"#fff", display:"flex", alignItems:"center",
                  justifyContent:"center", fontWeight:800, fontSize:".8rem" }}>{t.init}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:"0.85rem", color:P.text }}>{t.name}</div>
                  <div style={{ fontSize:"0.72rem", color:P.textLight }}>{t.role}</div>
                </div>
              </div>
            </blockquote>
          ))}
        </div>
      </Sec>

      {/* ══ FAQ ═══════════════════════════════════════════════ */}
      <Sec id="faq" bg={P.xxl}>
        <Head tag="FAQ" title="Frequently Asked Questions"/>
        <div style={{ maxWidth:760, margin:"0 auto", display:"flex", flexDirection:"column", gap:14 }}>
          {[
            { q:"How do I enrol my school?", a:"Simply contact us via phone, email or the form below. We discuss your school's size and needs, sign the agreement and set everything up — usually within 24 hours." },
            { q:"Is there a free trial?", a:"Yes. We offer a free demo session where we walk through the full platform with you. Contact us to schedule one." },
            { q:"Do teachers need to be tech-savvy?", a:"Not at all. VidyaTrack is designed to be extremely simple. If a teacher can use a smartphone, they can use VidyaTrack. We also provide full onboarding training." },
            { q:"Can parents see everything?", a:"Parents see their own child's homework, attendance, notices and calendar. They cannot see other students' data." },
            { q:"Is data secure?", a:"Yes. VidyaTrack uses Google Firebase with enterprise-grade encryption, role-based access controls and automatic backups. Your data is never shared." },
            { q:"Does it work offline?", a:"VidyaTrack requires an internet connection for real-time sync. However, previously loaded data is cached and accessible with limited connectivity." },
          ].map(({q,a})=>(
            <details key={q} style={{ background:"#fff", borderRadius:12,
              border:`1px solid ${P.border}`, padding:"16px 20px" }}>
              <summary style={{ fontWeight:700, fontSize:"0.95rem", color:P.text,
                cursor:"pointer", listStyle:"none", display:"flex",
                justifyContent:"space-between", alignItems:"center" }}>
                {q}
                <span style={{ color:P.light, fontSize:"1.2rem", lineHeight:1 }}>+</span>
              </summary>
              <p style={{ marginTop:12, fontSize:"0.87rem", color:P.textMed,
                lineHeight:1.75, marginBottom:0 }}>{a}</p>
            </details>
          ))}
        </div>
      </Sec>

      {/* ══ CONTACT ═══════════════════════════════════════════ */}
      <Sec id="contact" bg="#fff">
        <Head tag="GET IN TOUCH" title="Ready to Transform Your School?"
          sub="Contact us today. We will walk you through the platform, answer every question and handle the full setup."/>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"start", maxWidth:960, margin:"0 auto" }}>

          {/* Left — contact info */}
          <div>
            <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.2rem",
              color:P.text, marginBottom:8 }}>Talk to us directly</h3>
            <p style={{ color:P.textLight, fontSize:"0.9rem", lineHeight:1.75, marginBottom:28 }}>
              We are a dedicated team based in Kathmandu, Nepal. Reach out and we will respond within a few hours.
              No sales pressure — just an honest conversation about what VidyaTrack can do for your school.
            </p>

            {CONTACT_INFO.map(c=>(
              <div key={c.label} style={{ display:"flex", alignItems:"center", gap:14,
                marginBottom:18, padding:"14px 18px", borderRadius:12,
                background:P.xxl, border:`1px solid ${P.border}` }}>
                <span style={{ fontSize:"1.4rem" }}>{c.icon}</span>
                <div>
                  <div style={{ fontSize:"0.7rem", fontWeight:700, color:P.light,
                    textTransform:"uppercase", letterSpacing:".8px" }}>{c.label}</div>
                  {c.href ? (
                    <a href={c.href} style={{ fontSize:"0.93rem", fontWeight:600,
                      color:P.primary, textDecoration:"none" }}>{c.val}</a>
                  ) : (
                    <div style={{ fontSize:"0.93rem", fontWeight:600, color:P.text }}>{c.val}</div>
                  )}
                </div>
              </div>
            ))}

            <div style={{ marginTop:24, padding:"18px 20px", borderRadius:12,
              background:"linear-gradient(135deg,#054bb4,#2e5caf)", color:"#fff" }}>
              <div style={{ fontWeight:700, fontSize:"0.9rem", marginBottom:6 }}>
                📅 Book a Free Demo Session
              </div>
              <p style={{ fontSize:"0.82rem", opacity:.8, lineHeight:1.65, margin:"0 0 14px" }}>
                We will set up a screen-share demo tailored to your school's grade levels, teacher count and specific needs.
              </p>
              <a href="mailto:titaniumcode.np@gmail.com?subject=VidyaTrack Demo Request"
                style={{ display:"inline-block", background:"rgba(255,255,255,.15)",
                  color:"#fff", padding:"9px 18px", borderRadius:8, fontSize:"0.83rem",
                  fontWeight:700, textDecoration:"none", border:"1px solid rgba(255,255,255,.25)" }}>
                Email Us for Demo →
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div style={{ background:P.xxl, borderRadius:18, padding:"32px 28px", border:`1px solid ${P.border}` }}>
            {sent ? (
              <div style={{ textAlign:"center", padding:"48px 20px" }}>
                <div style={{ fontSize:"3rem", marginBottom:16 }}>✅</div>
                <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.2rem",
                  color:P.text, marginBottom:10 }}>We got your message!</h3>
                <p style={{ color:P.textLight, fontSize:"0.9rem", lineHeight:1.7 }}>
                  Thanks for reaching out. We will contact you within a few hours to discuss your school's enrollment and schedule a demo.
                </p>
              </div>
            ) : (
              <form onSubmit={submitContact} noValidate>
                <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1.05rem",
                  color:P.text, marginBottom:20 }}>Send us a message</h3>

                {formErr && (
                  <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8,
                    padding:"10px 14px", fontSize:"0.83rem", color:"#dc2626", marginBottom:16 }}>
                    {formErr}
                  </div>
                )}

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                  <div>
                    <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                      display:"block", marginBottom:5 }}>Your Name *</label>
                    <input style={inp} placeholder="Ram Prasad Sharma"
                      value={contact.name} onChange={e=>setContact(c=>({...c,name:e.target.value}))}
                      onFocus={e=>e.target.style.borderColor=P.primary}
                      onBlur={e=>e.target.style.borderColor=P.border}/>
                  </div>
                  <div>
                    <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                      display:"block", marginBottom:5 }}>Email Address *</label>
                    <input style={inp} type="email" placeholder="principal@school.edu.np"
                      value={contact.email} onChange={e=>setContact(c=>({...c,email:e.target.value}))}
                      onFocus={e=>e.target.style.borderColor=P.primary}
                      onBlur={e=>e.target.style.borderColor=P.border}/>
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                  <div>
                    <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                      display:"block", marginBottom:5 }}>School Name *</label>
                    <input style={inp} placeholder="Shree Saraswati School"
                      value={contact.school} onChange={e=>setContact(c=>({...c,school:e.target.value}))}
                      onFocus={e=>e.target.style.borderColor=P.primary}
                      onBlur={e=>e.target.style.borderColor=P.border}/>
                  </div>
                  <div>
                    <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                      display:"block", marginBottom:5 }}>Phone Number</label>
                    <input style={inp} placeholder="+977-98XXXXXXXX"
                      value={contact.phone} onChange={e=>setContact(c=>({...c,phone:e.target.value}))}
                      onFocus={e=>e.target.style.borderColor=P.primary}
                      onBlur={e=>e.target.style.borderColor=P.border}/>
                  </div>
                </div>

                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                    display:"block", marginBottom:5 }}>Interested Plan</label>
                  <select style={{ ...inp }}
                    value={contact.plan} onChange={e=>setContact(c=>({...c,plan:e.target.value}))}
                    onFocus={e=>e.target.style.borderColor=P.primary}
                    onBlur={e=>e.target.style.borderColor=P.border}>
                    <option value="">Select a plan (optional)</option>
                    <option value="Silver">🥈 Silver — Rs. 149/student/mo (up to 300 students)</option>
                    <option value="Gold">🥇 Gold — Rs. 199/student/mo (up to 800 students)</option>
                    <option value="Platinum">💎 Platinum — Unlimited (contact for pricing)</option>
                    <option value="Not sure">Not sure yet — need more info</option>
                  </select>
                </div>

                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:"0.75rem", fontWeight:700, color:P.textMed,
                    display:"block", marginBottom:5 }}>Message</label>
                  <textarea style={{ ...inp, resize:"vertical", minHeight:90 }}
                    placeholder="Tell us about your school — number of students, grades, any specific requirements..."
                    value={contact.message} onChange={e=>setContact(c=>({...c,message:e.target.value}))}
                    onFocus={e=>e.target.style.borderColor=P.primary}
                    onBlur={e=>e.target.style.borderColor=P.border}/>
                </div>

                <button type="submit" disabled={sending}
                  style={{ width:"100%", padding:"13px 0", borderRadius:10,
                    background:"linear-gradient(135deg,#054bb4,#2e5caf)", color:"#fff",
                    border:"none", cursor:sending?"not-allowed":"pointer",
                    fontWeight:700, fontSize:"0.95rem", fontFamily:"Inter,sans-serif",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                    boxShadow:"0 6px 20px rgba(5,75,180,.26)", opacity:sending?.7:1,
                    transition:"all .2s" }}>
                  {sending ? "Sending…" : <><span>Send Message</span><FiArrowRight/></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </Sec>

      </main>

      {/* ══ FOOTER ════════════════════════════════════════════ */}
      <LandingFooter scroll={scroll}/>

      {/* ── Responsive styles ── */}
      <style>{`
        .vt-nav-links,.vt-nav-cta { display:flex!important }
        .vt-hamburger { display:none!important }

        @media(max-width:1024px){
          .vt-nav-links,.vt-nav-cta { display:none!important }
          .vt-hamburger { display:flex!important }
        }
        @media(max-width:768px){
          #hero { padding-top:80px!important; padding-bottom:52px!important }
          #contact > div > div[style*="grid-template-columns:1fr 1fr"] { grid-template-columns:1fr!important }
          div[style*="repeat(3,1fr)"][style*="gap:24px"] { grid-template-columns:1fr!important }
          div[style*="scale(1.04)"] { transform:none!important }
          footer > div > div[style*="2fr 1fr"] { grid-template-columns:1fr 1fr!important }
          section { padding-left:16px!important; padding-right:16px!important }
          div[style*="auto-fill,minmax(300px"] { grid-template-columns:1fr!important }
          div[style*="auto-fill,minmax(280px"] { grid-template-columns:1fr!important }
        }
        @media(max-width:520px){
          div[style*="auto-fill,minmax(240px"] { grid-template-columns:1fr!important }
          div[style*="auto-fill,minmax(250px"] { grid-template-columns:1fr!important }
          div[style*="1fr 1fr"][style*="gap:12px"] { grid-template-columns:1fr!important }
          footer > div > div[style*="2fr 1fr"] { grid-template-columns:1fr!important }
        }
        details summary::-webkit-details-marker { display:none }
        details[open] summary span { transform:rotate(45deg); display:inline-block }
      `}</style>
    </div>
  );
}
